// S9 · Terminology settings — the PURE model (Module 02 §2.5, §4 S9, D-032/D-035).
//
// `GET /v1/terminology?locale=` answers ONE locale at a time, already resolved
// tenant → pack → base, with the EFFECTIVE `source` that won (D-032: `base` is
// computed, never stored). S9 is a term × locale grid (six locales since
// D-590), so the screen runs one read per locale and this module stitches
// them into one row per canonical key.
//
// Kept pure so the merge, the search and the reset rule are unit-tested without
// a DOM: they are the parts that would silently lie if they were wrong (a cell
// attributed to the wrong layer would make a tenant think a rename came from
// their pack when it came from their own edit).

import type { components } from "@assessly/api-client";
import { SERVER_TEXT_LOCALES, type ServerTextLocale } from "@assessly/contracts/locales";

type S = components["schemas"];

export type TermEntry = S["TerminologyEntry"];
export type TermKey = TermEntry["term_key"];
export type TermSource = TermEntry["source"];

/** The locales the contract accepts (`terminology_overrides.locale` CHECK):
 * the ones server text is written in, from the locale registry, in its
 * order: all six since L4 wrote zh-Hant and hi (D-590). A locale the registry
 * adds before its base strings land stays out of the grid until it is taken
 * off SERVER_TEXT_PENDING. */
export const TERM_LOCALES = SERVER_TEXT_LOCALES;
export type TermLocaleCode = ServerTextLocale;

export interface TermCell {
  label: string;
  /** Which layer answered: the tenant's own edit, the industry pack, or the
   * built-in base label. */
  source: TermSource;
}

export interface TermRow {
  termKey: TermKey;
  /** A locale is absent only while its feed is still loading or failed — the
   * server always resolves all 61 keys for a locale it answered (§5). */
  cells: Partial<Record<TermLocaleCode, TermCell>>;
}

export type LocaleFeeds = Partial<Record<TermLocaleCode, readonly TermEntry[] | undefined>>;

/**
 * Merge the per-locale feeds into one row per term key.
 *
 * Row ORDER follows the first feed that answered, in the order the API sent it
 * (the frozen 61-key catalog order, `packages/contracts/terminology.ts`), then
 * any key a later locale carries that the first did not — so a partially loaded
 * grid never reshuffles under the user as the remaining locales arrive.
 */
export function buildTermRows(feeds: LocaleFeeds): TermRow[] {
  const order: TermKey[] = [];
  const byKey = new Map<TermKey, TermRow>();

  for (const locale of TERM_LOCALES) {
    for (const entry of feeds[locale] ?? []) {
      let row = byKey.get(entry.term_key);
      if (row === undefined) {
        row = { termKey: entry.term_key, cells: {} };
        byKey.set(entry.term_key, row);
        order.push(entry.term_key);
      }
      row.cells[locale] = { label: entry.label, source: entry.source };
    }
  }

  return order.flatMap((key) => {
    const row = byKey.get(key);
    return row === undefined ? [] : [row];
  });
}

/** The source filter chips: every layer, plus "only what I changed". */
export type SourceFilter = TermSource | "all" | "overridden";

export interface TermFilter {
  /** Free text, matched case-insensitively against the key and every label. */
  q: string;
  source: SourceFilter;
}

export function isOverridden(row: TermRow): boolean {
  return TERM_LOCALES.some((locale) => row.cells[locale]?.source === "tenant");
}

function matchesSource(row: TermRow, source: SourceFilter): boolean {
  if (source === "all") return true;
  if (source === "overridden") return isOverridden(row);
  return TERM_LOCALES.some((locale) => row.cells[locale]?.source === source);
}

function matchesQuery(row: TermRow, needle: string): boolean {
  if (row.termKey.toLowerCase().includes(needle)) return true;
  return TERM_LOCALES.some(
    (locale) => row.cells[locale]?.label.toLowerCase().includes(needle) ?? false,
  );
}

/** Filter the grid. Both filters AND together; an empty query matches all. */
export function filterTermRows(rows: readonly TermRow[], filter: TermFilter): TermRow[] {
  const needle = filter.q.trim().toLowerCase();
  return rows.filter(
    (row) =>
      matchesSource(row, filter.source) && (needle.length === 0 || matchesQuery(row, needle)),
  );
}

export interface TermStats {
  /** Rows in the grid. */
  terms: number;
  /** Cells (key × locale) answered by each layer. */
  tenant: number;
  pack: number;
  base: number;
  /** Rows carrying at least one tenant override. */
  overriddenTerms: number;
}

export function termStats(rows: readonly TermRow[]): TermStats {
  let tenant = 0;
  let pack = 0;
  let base = 0;
  let overriddenTerms = 0;
  for (const row of rows) {
    if (isOverridden(row)) overriddenTerms += 1;
    for (const locale of TERM_LOCALES) {
      const source = row.cells[locale]?.source;
      if (source === "tenant") tenant += 1;
      else if (source === "pack") pack += 1;
      else if (source === "base") base += 1;
    }
  }
  return { terms: rows.length, tenant, pack, base, overriddenTerms };
}

/**
 * Whether "reset" does anything for a cell.
 *
 * `DELETE /v1/terminology/{term_key}?locale=` removes the TENANT row. A cell
 * already answered by the pack or the base has no tenant row to remove, so the
 * control is not offered (the API would answer 200 with an unchanged label —
 * a no-op button is a lie about what will happen).
 */
export function canReset(cell: TermCell | undefined): boolean {
  return cell?.source === "tenant";
}

/** The label a tenant edit must satisfy (contract: 1–80 chars, §2.5 CHECK). */
export const LABEL_MAX = 80;

export type LabelProblem = "empty" | "too_long" | null;

/** Client mirror of the contract's label rule; the server re-validates. */
export function validateLabel(value: string): LabelProblem {
  const trimmed = value.trim();
  if (trimmed.length === 0) return "empty";
  if (trimmed.length > LABEL_MAX) return "too_long";
  return null;
}
